import React, { useState, useEffect, ChangeEvent } from 'react';
import { Row, Col, Alert } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { DetailItem } from '../../../../features/details';
import { updateSite, addSite } from '../../../../services/api/site';
import { Site, ApiError } from '../../../../services/openapi';
import { useKeycloak } from '@react-keycloak/web'
import { PropertyInfo } from '../../../../services/openapi/models/PropertyInfo';
import { Link, useNavigate } from "react-router-dom";
import { NotImplementedWarningModal } from '../../../../components/NotImplementedWarningModal';
import Dropzone from '../../../../components/Dropzone';
import JsonDownload from '../../../../components/exports/JsonDownload';
import { getScreenConfig } from '../../../../services/api/screenConfig';
import { getChargePointOperatorBySiteId, updateCpo, addCpo } from '../../../../services/api/chargePointOperator';
import { getEcrBasicDataBySiteId } from '../../../../services/api/ecrBasicSiteData';
import { setPendingSelectedId } from '../../../../hooks/pendingSelection';
import { useTranslation } from 'react-i18next';

const SiteDetails = (): JSX.Element => {
        const [site, setSite] = useState<any>();
        const [showInputEle, setShowInputEle] = useState<boolean>(false);
        const [showModal, setShowModal] = useState(false);
        const [screenConfig, setScreenConfig] = useState<string>('');
        const [isDragEnter, setIsDragEnter] = useState<boolean>(false);
        const [errorMsg, setErrorMsg] = useState<string[]>([]);
        const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
        const [cpo, setCpo] = useState<any>();
        const [cpoId, setCpoId] = useState<string>("");
        const [ecrBasicData, setEcrBasicData] = useState<any>();
        const [submitted, setSubmitted] = useState(false);

        const { keycloak } = useKeycloak();
        const token = keycloak?.token || '';

        const navigate = useNavigate();
        const { t } = useTranslation();

        const navigateBack = () => navigate('/dashboard/lists/sites');

        const setSiteData = (data: Site) => localStorage.setItem('Site', JSON.stringify(data));

        const pushErrorMessages = (errors: any) => {
            try {
                Object.entries<Record<string, any>>(errors).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg([...errorMsg]); }) });
            } catch (ex) {
                errorMsg.push("Error handling response");
                setErrorMsg([...errorMsg]);
            }
        }

        const toggleInputElements = (show: boolean) => {
            setShowInputEle(show);
            if (show) {
                document.querySelector('.errorMsg')?.classList.add("d-none");
                errorMsg.length = 0;
            }
        }

        const showErrorMessages = (msgs?: string[]) => {
            document.querySelector('.errorMsg')?.classList.remove("d-none");
            if (msgs && msgs.length > 0) {
                msgs.forEach(m => errorMsg.push(m));
                setErrorMsg([...errorMsg]);
            }
        }

        const init = (item: any) => {
            if (!item) return;
            if (item && item.id && item.id !== 0) {
                getChargePointOperatorBySiteId(token, item.siteId).then((c) => { setCpo(c); setCpoId(c?.chargePointOperatorId ?? ''); }).catch(() => { });
                if (item.type === 'Basic') {
                    getEcrBasicDataBySiteId(token, item.siteId).then(d => setEcrBasicData(d)).catch(() => { });
                }
            }
        }

        useEffect(() => {
            if (site == null) {
                const json = localStorage.getItem('Site') ?? "";
                const item = JSON.parse(json);
                setSite(item);
                init(item);

                if (item["id"] === 0) {
                    toggleInputElements(true);
                }
            }            
        }, [isRefreshing, site]);

        function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
            setSubmitted(true);
            e.preventDefault();
            document.querySelector('.errorMsg')?.classList.add("d-none");
            errorMsg.length = 0;

            if (e.currentTarget.checkValidity()) {
                let s: Site = site;
                setSiteData(s);
                if (s.id === 0) {
                    addSite(token, s).then((id) => {
                        if (id) {
                            let ns: Site = { ...(s as Site)} as Site;
                            ns.id = id;
                            setSite(ns);
                            setSiteData(ns);
                            setIsRefreshing(false);
                            navigateBack();
                        }
                    }).catch((error: ApiError) => {
                        try {
                            document.querySelector('.errorMsg')?.classList.remove("d-none");
                            pushErrorMessages(JSON.parse(error.body)["errors"]);
                            showErrorMessages();
                        } catch (ex) {
                            console.log(ex);
                            showErrorMessages(["Error handling response"]);
                        }
                    });
                } else {
                    updateSite(token, s.id ?? 0, s).then(() => {
                        if (s.type === "Charging" && cpo) {
                            setIsRefreshing(true);
                            let s2: Site = site;
                            cpo.siteId = s2.siteId;
                            if (cpoId) {
                                updateCpo(token, cpoId, cpo).then(() => { setCpoId(cpo.chargePointOperatorId); navigateBack(); }).catch((error: ApiError) => {
                                    try {
                                        document.querySelector('.errorMsg')?.classList.remove("d-none");
                                        pushErrorMessages(JSON.parse(error.body)["errors"]);
                                        showErrorMessages();
                                    } catch (ex) {
                                        console.log(ex);
                                        showErrorMessages(["Error handling response"]);
                                    }
                                });
                            } else {
                                setCpoId(cpo.chargePointOperatorId);
                                addCpo(token, cpo).then(() => navigateBack()).catch((error: ApiError) => {
                                    try {
                                        pushErrorMessages(JSON.parse(error.body)["errors"]);
                                        showErrorMessages();
                                    } catch (ex) {
                                        console.log(ex);
                                        showErrorMessages(["Error handling response"]);
                                    }
                                });
                            }
                        } else {
                            navigateBack();
                        }
                    }).catch((error: ApiError) => {
                        try {
                            document.querySelector('.errorMsg')?.classList.remove("d-none");
                            pushErrorMessages(JSON.parse(error.body)["errors"]);
                            showErrorMessages();
                        } catch (ex) {
                            console.log(ex);
                            showErrorMessages(["Error handling response"]);
                        }
                    });
                }

                setShowInputEle(!showInputEle);
            }
        }

        let idx = 0;

        const handleFileContents = (contents: string) => setScreenConfig(contents);

        const handleJsonDownload = (): Promise<string> => {
            return getScreenConfig(token, site?.siteId).catch((error: ApiError) => {
                try {
                    pushErrorMessages(JSON.parse(error.body)["errors"]);
                    showErrorMessages();
                    throw error;
                }
                catch (ex) {
                    console.log(ex);
                    showErrorMessages(["Error handling response"]);
                    throw ex;
                }
            });
        }

        function getBackgroundColor(isDragBox: boolean) {
            if (isDragBox && isDragEnter) return 'red';
            if (site) {
                let itemCount = Object.keys(site.propertyNames || {}).length;
                if (ecrBasicData) itemCount++;
                return (itemCount % 2) ? "#cfd5ea" : "#e9ebf5";
            }
            return "#e9ebf5";
        }

        return (
            <>
                <div className="">
                    <NotImplementedWarningModal
                        showModal={showModal}
                        handleClose={() => setShowModal(false)}
                    />
                </div>
                <Row className="">
                    <Col className="">
                        <Card className="rounded">
                            <Card.Body className="">
                                <form onSubmit={e => handleSubmit(e)}>
                                    <Row className="d-none d-md-block"><Col>&nbsp;</Col></Row>
                                    <Row className="d-none d-md-block"><Col>&nbsp;</Col></Row>                                    
                                    <Row className=""><Col sm="12"><h4 className="mb-2 qvend-title">Site{site?.id ? t('details') : (' ')+t('toevoegen')}</h4></Col></Row>
                                    <Row className=""><Col sm="12" xs="12" className="mt-2">
                                        <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                            <div>
                                                {site && Object.entries(site as PropertyInfo).filter(([key, value]) => site["propertyNames"]?.[key] !== undefined && (site["propertyNames"][key].editType !== '' || (site["propertyNames"][key].editType === '' && value !== null))).sort((a, b) => site["propertyNames"][a[0]].sortOrderLevel - site["propertyNames"][b[0]].sortOrderLevel).map(([key, value], index) => (
                                                    <DetailItem submitted={submitted} key={site["propertyNames"][key].sortOrderLevel} propertyName={site["propertyNames"][key].name} defaultValue={value ?? ""} rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"} editType={site["propertyNames"][key].editType} pattern={site["propertyNames"][key].valPattern} hideOnNull={site["propertyNames"][key].hideOnNull}
                                                        handleDoubleClick={() => setShowInputEle(true)}
                                                        handleChange={(e: ChangeEvent<HTMLInputElement>) => { site[key] = (e.target.value); setSite(site); }} showInputEle={showInputEle} possibilities={site["propertyNames"][key].predefinedValues} conditionToShowReferenceValue={site[site["propertyNames"][key].conditionToShowFieldName]} conditionToShowValue={site["propertyNames"][key].conditionToShowValue}></DetailItem>
                                                ))}

                                                {(site as Site)?.type == "Charging" && cpo && Object.entries(cpo as PropertyInfo).filter(([key, value]) => cpo["propertyNames"]?.[key] !== undefined && (cpo["propertyNames"][key].editType !== '' || (cpo["propertyNames"][key].editType === '' && value !== null))).sort((a, b) => cpo["propertyNames"][a[0]].sortOrderLevel - cpo["propertyNames"][b[0]].sortOrderLevel).map(([key, value], index) => (
                                                    <DetailItem submitted={submitted} key={cpo["propertyNames"][key].sortOrderLevel} propertyName={cpo["propertyNames"][key].name} defaultValue={value} rowColor={((cpo["propertyNames"][key].hideOnNull && (value == null || value === undefined) ? idx : idx++)) % 2 ? "#cfd5ea" : "#e9ebf5"} editType={cpo["propertyNames"][key].editType} pattern={cpo["propertyNames"][key].valPattern} hideOnNull={cpo["propertyNames"][key].hideOnNull}
                                                        handleDoubleClick={() => setShowInputEle(true)}
                                                        handleChange={(e: ChangeEvent<HTMLInputElement>) => { cpo[key] = (e.target.value); setCpo(cpo); }} showInputEle={showInputEle} possibilities={cpo["propertyNames"][key].predefinedValues} conditionToShowReferenceValue={cpo[cpo["propertyNames"][key].conditionToShowFieldName]} conditionToShowValue={cpo["propertyNames"][key].conditionToShowValue}></DetailItem>
                                                ))}

                                                {(site as Site)?.type == "Basic" && ecrBasicData && Object.entries(ecrBasicData as PropertyInfo).filter(([key, value]) => ecrBasicData["propertyNames"]?.[key] !== undefined && (ecrBasicData["propertyNames"][key].editType !== '' || (ecrBasicData["propertyNames"][key].editType === '' && value !== null))).sort((a, b) => ecrBasicData["propertyNames"][a[0]].sortOrderLevel - ecrBasicData["propertyNames"][b[0]].sortOrderLevel).map(([key, value], index) => (
                                                    <DetailItem submitted={submitted} key={ecrBasicData["propertyNames"][key].sortOrderLevel} propertyName={ecrBasicData["propertyNames"][key].name} defaultValue={value} rowColor={((ecrBasicData["propertyNames"][key].hideOnNull && (value == null || value === undefined) ? idx : idx++)) % 2 ? "#cfd5ea" : "#e9ebf5"} editType={ecrBasicData["propertyNames"][key].editType} pattern={ecrBasicData["propertyNames"][key].valPattern} hideOnNull={ecrBasicData["propertyNames"][key].hideOnNull}
                                                        handleDoubleClick={() => setShowInputEle(true)}
                                                        handleChange={(e: ChangeEvent<HTMLInputElement>) => { }} showInputEle={showInputEle} possibilities={ecrBasicData["propertyNames"][key].predefinedValues} conditionToShowReferenceValue={ecrBasicData[ecrBasicData["propertyNames"][key].conditionToShowFieldName]} conditionToShowValue={ecrBasicData["propertyNames"][key].conditionToShowValue}></DetailItem>
                                                ))}

                                                <Row className="qvend-table-font" style={{ background: getBackgroundColor(false), color: "black" }}>
                                                    <Col xl={6} lg={6} md={6} sm={6} xs={6} className="qvend-table table-column-first q-vend-detail-table-col">Screen config</Col>
                                                    <Col xl={6} lg={6} md={6} sm={6} xs={6} className="qvend-table q-vend-detail-table-col" style={{ backgroundColor: getBackgroundColor(true) }}>
                                                        {showInputEle ? (<Dropzone handleFilename={() => { }} handleFileContents={handleFileContents} onDragEnter={(e: DragEvent) => { setIsDragEnter(true); }} onDragLeave={(e: DragEvent) => { setIsDragEnter(false); }} />) : site && (<JsonDownload filename={`site-${site.siteId}-screens.json`} onGetData={handleJsonDownload} />)}
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </Col></Row>

                                    <Row className="d-none errorMsg"><Col>{errorMsg.map((item, index) => (<Alert key={index} variant="warning" className="alert" role="alert"><span>{item}</span></Alert>))}</Col></Row>

                                    <Row style={{ paddingTop: "12px" }}><Col></Col><Col className="text-end"></Col></Row>

                                    <Row style={{ paddingTop: "3px" }}>
                                        <Col>
                                            <Link to="/dashboard/lists/sites" onClick={() => setPendingSelectedId("sites", site?.id)}>
                                                <button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px', backgroundColor: "red" }} onClick={() => { }}>{showInputEle ? t("button.cancel") : t("button.back")}</button>
                                            </Link>
                                        </Col>
                                        <Col className="text-end">
                                            {showInputEle ? (<button type="submit" className="btn btn-fixed-width" style={{ fontSize: '11px' }} onClick={e => setSubmitted(true)}>{site && site.id > 0 ? t("button.save") : t("button.add")}</button>) : (<button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px' }} onClick={(e) => { e.preventDefault(); setShowInputEle(true); }}>{t("button.edit")}</button>)}
                                        </Col>
                                    </Row>

                                </form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    }

    export default SiteDetails;
